import { Alert, Select, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { rawApi } from '../api/client';
import { listKey } from '../api/queryKeys';
import { enumLabel } from '../lib/enums';
import { formatMoney } from '../lib/format';

/**
 * "Which box did that cash come out of?" — the eleven-in-the-morning half of paying somebody.
 *
 * The water and the ice arrive before anybody has a drawer open, and they are paid out of the
 * change box, which is precisely what the owner keeps it stocked for. Until the box payment
 * existed, the only honest record was "Some other way" — a payment that named no container at all,
 * so the cash left the box and nothing anywhere said so. This asks the one question that record
 * needs: which box, the float box or the safe. The movement lands on that box's own ledger and no
 * drawer is touched.
 *
 * The two boxes carry different floors, and the picker says so where it matters. The safe will not
 * hand over more than it is recorded as holding — the same rule a transfer and a deposit out of it
 * already meet — so a payment past that figure is flagged before the press instead of arriving as
 * a server refusal after it. The float box has no floor: its derived figure is what has passed
 * through it, not what is in it, and the standing-float count is where it answers for itself.
 */

const BOXES_PATH = '/admin/v1/cash/locations';
const BALANCES_PATH = '/admin/v1/cash/balances';

export interface CashBoxChoice {
  id: string;
  name: string;
  kind: 'drawer' | 'cajaita' | 'safe';
  active: boolean;
}

interface BoxBalance {
  cashLocationId: string;
  balance: { usdMinor: number; hnlMinor: number };
}

/** The boxes a payment can come out of: everything that is not a drawer and is still in service. */
export function useCashBoxes(enabled: boolean) {
  const query = useQuery({
    queryKey: listKey(BOXES_PATH),
    queryFn: () => rawApi<CashBoxChoice[]>('GET', BOXES_PATH),
    enabled,
  });
  const boxes = (query.data ?? []).filter((b) => b.kind !== 'drawer' && b.active);
  return {
    boxes,
    loading: query.isLoading,
    /** True once we know the answer and the answer is "no box exists to pay out of". */
    none: !query.isLoading && query.isSuccess && boxes.length === 0,
  };
}

export function CashOutOfBox({
  boxes,
  loading,
  value,
  onChange,
  currency,
  /** What is about to be handed over, so a safe that cannot cover it is flagged before saving. */
  amountMinor,
  disabled,
}: {
  boxes: CashBoxChoice[];
  loading: boolean;
  value: string | null;
  onChange: (cashLocationId: string | null) => void;
  currency: string;
  amountMinor: number;
  disabled?: boolean;
}) {
  const chosen = boxes.find((b) => b.id === value);
  // Only the safe has a floor to warn about, so the balances are only read when a safe is chosen.
  const balances = useQuery({
    queryKey: listKey(BALANCES_PATH),
    queryFn: () => rawApi<BoxBalance[]>('GET', BALANCES_PATH),
    enabled: chosen?.kind === 'safe',
    staleTime: 15_000,
  });

  if (loading) {
    return (
      <Text size="sm" c="dimmed">
        Checking which boxes there are…
      </Text>
    );
  }

  if (boxes.length === 0) {
    return (
      <Alert color="yellow" variant="light" title="No box to pay out of">
        No float box or safe has been set up, so there is nothing this cash can have come out of. Add one
        under Register &amp; cash first, or record the payment some other way.
      </Alert>
    );
  }

  const held = (() => {
    if (chosen?.kind !== 'safe') return null;
    const row = (balances.data ?? []).find((b) => b.cashLocationId === chosen.id);
    if (!row) return null;
    return currency === 'USD' ? row.balance.usdMinor : row.balance.hnlMinor;
  })();
  const short = held !== null && amountMinor > held;

  return (
    <>
      <Select
        label="Which box it came out of"
        description="The payment goes on that box's own ledger — no drawer is touched and no register needs to be open"
        required
        data={boxes.map((b) => {
          // The box's own name, with what kind of box it is beside it — unless the name already
          // says so, where "Safe · Safe" would read as a stutter.
          const kind = enumLabel('cashBoxKind', b.kind);
          return {
            value: b.id,
            label: b.name.toLowerCase() === kind.toLowerCase() ? b.name : `${b.name} · ${kind}`,
          };
        })}
        value={value}
        onChange={onChange}
        allowDeselect={false}
        comboboxProps={{ withinPortal: true }}
        disabled={disabled}
      />
      {chosen?.kind === 'safe' && held !== null && (
        <Text size="sm" c={short ? 'red' : 'dimmed'} mt={-8}>
          {short
            ? `The safe is only recorded as holding ${formatMoney(held, currency)} — a payment past that is refused. If the notes really came out of it, record what put them there first.`
            : `Recorded as holding ${formatMoney(held, currency)} — it will not hand over more than that.`}
        </Text>
      )}
    </>
  );
}
